import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  DataTable,
  ErrorNote,
  Field,
  IdChip,
  Input,
  Pagination,
  Select,
  Skeleton,
  Tabs,
  type Column,
} from '../components/ui';
import { api, compact } from '../lib/api';
import { TYPING_DEBOUNCE_MS, useDebounced } from '../lib/debounce';
import {
  formatNumber,
  formatRelative,
  formatSchedule,
  humanize,
} from '../lib/format';
import { useT } from '../lib/i18n';
import {
  BackButton,
  useOpenRecord,
  useTrail,
  type Trail,
} from '../lib/navigation';
import type { AdminAlarm, AdminAlarmEvent, Paginated } from '../lib/types';
import { AlarmDrawer } from './alarms/AlarmDrawer';
import { EventDrawer } from './alarms/AlarmEvents';
import { OfflineRewards } from './alarms/OfflineRewards';
import { ReviewQueue } from './alarms/ReviewQueue';

type AlarmsTab = 'list' | 'review' | 'offline';

function tabOf(value: string | null): AlarmsTab {
  return value === 'review' || value === 'offline' ? value : 'list';
}

/**
 * Every alarm in the system, and the claims waiting on a person.
 *
 * The columns are chosen for the two questions this page gets opened with:
 * "what is this user's alarm actually set to?" and "why is this account
 * holding credits?" — which is why the stake is on the row rather than behind
 * a click.
 *
 * Which record is open lives in the url rather than in state, so an alarm
 * reached from a user, or an event reached from an alarm, can be reloaded,
 * linked to, and stepped back out of.
 */
export function AlarmsPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const trail = useTrail();
  const open = useOpenRecord();

  const [filters, setFilters] = useState<{
    type?: string;
    enabled?: string;
    holdingOnly?: boolean;
    includeDeleted?: boolean;
    search?: string;
    sort?: 'lastEvent';
  }>({});
  const [page, setPage] = useState(1);

  const alarmId = params.get('alarm');
  const eventId = params.get('event');
  // The device scope belongs in the url: it is how a user's drawer hands this
  // page its question, and it has to survive the trip. Device ids are what
  // support is handed, so that is what this page is searched by.
  const scopedDeviceId = params.get('deviceId') ?? '';
  // Typed a character at a time; one request per pause, not per keystroke.
  const deviceQuery = useDebounced(scopedDeviceId.trim(), TYPING_DEBOUNCE_MS);
  const tab = tabOf(params.get('tab'));

  const query = useMemo(
    () =>
      compact({
        ...filters,
        deviceId: deviceQuery || undefined,
        enabled: filters.enabled === '' ? undefined : filters.enabled,
        page,
        limit: 25,
      }),
    [filters, deviceQuery, page],
  );

  const alarms = useQuery({
    queryKey: ['alarms', query],
    queryFn: () => api.get<Paginated<AdminAlarm>>('/api/alarms', query),
    enabled: tab === 'list',
  });

  const review = useQuery({
    queryKey: ['review-count'],
    queryFn: () =>
      api.get<Paginated<AdminAlarmEvent>>('/api/events/review', { limit: 1 }),
    refetchInterval: 60_000,
  });

  const set = (next: Partial<typeof filters>) => {
    setFilters((current) => ({ ...current, ...next }));
    setPage(1);
  };

  /** Rewrites the url's own state — the scope, the tab, the open record. */
  const updateParams = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };

  const setScopedDevice = (deviceId: string) => {
    updateParams((next) => {
      if (deviceId) next.set('deviceId', deviceId);
      else next.delete('deviceId');
      // Typing over the scope by hand abandons whatever walk brought it here.
      next.delete('from');
    });
    setPage(1);
  };

  const closeRecord = () =>
    updateParams((next) => {
      next.delete('alarm');
      next.delete('event');
      next.delete('from');
    });

  const columns: Column<AdminAlarm>[] = [
    {
      key: 'label',
      header: t('alarms.column.alarm'),
      render: (alarm) => (
        <div className="flex flex-wrap items-center gap-2">
          <span className="tnum font-semibold">{alarm.time}</span>
          <span className="text-ink-2">
            {alarm.label || t('alarms.noLabel')}
          </span>
          {!alarm.enabled && <Badge tone="neutral">{t('common.off')}</Badge>}
          {alarm.isDeleted && (
            <Badge tone="danger">{t('alarms.deletedBadge')}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'schedule',
      header: t('alarms.column.schedule'),
      desktopOnly: true,
      render: (alarm) => (
        <span className="text-ink-2">
          {formatSchedule(alarm.recurrence.label)}
        </span>
      ),
    },
    {
      key: 'type',
      header: t('alarms.column.type'),
      render: (alarm) => (
        <Badge tone={alarm.type === 'reward' ? 'brand' : 'neutral'}>
          {humanize(alarm.type)}
        </Badge>
      ),
    },
    {
      key: 'challenge',
      header: t('alarms.column.challenge'),
      desktopOnly: true,
      render: (alarm) => (
        <span className="text-ink-2">
          {humanize(alarm.challengeType)} · {humanize(alarm.challengeDifficulty)}
        </span>
      ),
    },
    {
      key: 'held',
      header: t('alarms.column.staked'),
      numeric: true,
      render: (alarm) => formatNumber(alarm.heldAmount),
    },
    {
      key: 'record',
      header: t('alarms.column.record'),
      numeric: true,
      render: (alarm) =>
        `${alarm.rewardEarnedCount} / ${alarm.rewardMissedCount}`,
    },
    {
      key: 'ring',
      header: t('alarms.column.lastRang'),
      numeric: true,
      desktopOnly: true,
      render: (alarm) => (
        <span className="text-ink-2">{formatRelative(alarm.lastRingAt)}</span>
      ),
    },
    {
      key: 'event',
      header: t('alarms.column.lastEvent'),
      numeric: true,
      render: (alarm) => (
        <span className="text-ink-2">{formatRelative(alarm.lastEventAt)}</span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('alarms.title')}
        description={t('alarms.description')}
      />

      <Tabs
        label={t('alarms.pageTabsLabel')}
        value={tab}
        onChange={(next) =>
          updateParams((search) => {
            if (next === 'list') search.delete('tab');
            else search.set('tab', next);
          })
        }
        tabs={[
          { id: 'list', label: t('alarms.tab.all') },
          {
            id: 'review',
            label: t('events.tab.review'),
            badge: review.data?.total ?? 0,
          },
          { id: 'offline', label: t('offline.tab') },
        ]}
      />

      {tab === 'review' ? (
        <ReviewQueue />
      ) : tab === 'offline' ? (
        <OfflineRewards />
      ) : (
        <div className="space-y-4">
          {scopedDeviceId && (
            <ScopeNote
              deviceId={scopedDeviceId}
              trail={trail}
              onClear={() => setScopedDevice('')}
            />
          )}

          <div className="grid gap-3 rounded-xl border border-line bg-surface p-3.5 sm:grid-cols-2 sm:p-4 lg:grid-cols-3 xl:grid-cols-6">
            <Field label={t('alarms.filter.label')}>
              <Input
                value={filters.search ?? ''}
                onChange={(event) =>
                  set({ search: event.target.value || undefined })
                }
                placeholder={t('common.searchPlaceholder')}
              />
            </Field>
            <Field label={t('common.deviceId')}>
              <Input
                className="font-mono text-xs"
                value={scopedDeviceId}
                onChange={(event) => setScopedDevice(event.target.value)}
                placeholder={t('common.pasteDeviceId')}
              />
            </Field>
            <Field label={t('alarms.filter.type')}>
              <Select
                value={filters.type ?? ''}
                onChange={(event) =>
                  set({ type: event.target.value || undefined })
                }
              >
                <option value="">{t('common.any')}</option>
                <option value="reward">{t('alarms.type.reward')}</option>
                <option value="infinite">{t('alarms.type.infinite')}</option>
              </Select>
            </Field>
            <Field label={t('alarms.filter.enabled')}>
              <Select
                value={filters.enabled ?? ''}
                onChange={(event) => set({ enabled: event.target.value })}
              >
                <option value="">{t('common.any')}</option>
                <option value="true">{t('common.on')}</option>
                <option value="false">{t('common.off')}</option>
              </Select>
            </Field>
            <Field label={t('alarms.filter.show')}>
              <Select
                value={
                  filters.holdingOnly
                    ? 'holding'
                    : filters.includeDeleted
                      ? 'deleted'
                      : ''
                }
                onChange={(event) =>
                  set({
                    holdingOnly: event.target.value === 'holding' || undefined,
                    includeDeleted:
                      event.target.value === 'deleted' || undefined,
                  })
                }
              >
                <option value="">{t('alarms.filter.live')}</option>
                <option value="holding">{t('alarms.filter.holding')}</option>
                <option value="deleted">{t('alarms.filter.deleted')}</option>
              </Select>
            </Field>
            <Field label={t('alarms.filter.sort')}>
              <Select
                value={filters.sort ?? ''}
                onChange={(event) =>
                  set({
                    sort:
                      event.target.value === 'lastEvent'
                        ? 'lastEvent'
                        : undefined,
                  })
                }
              >
                <option value="">
                  {scopedDeviceId
                    ? t('alarms.sort.defaultScoped')
                    : t('alarms.sort.default')}
                </option>
                <option value="lastEvent">{t('alarms.sort.lastEvent')}</option>
              </Select>
            </Field>
          </div>

          {alarms.isError && <ErrorNote error={alarms.error} />}

          <Card>
            {alarms.isPending ? (
              <Skeleton className="h-96" />
            ) : (
              <>
                <DataTable
                  columns={columns}
                  rows={alarms.data?.items ?? []}
                  rowKey={(alarm) => alarm.id}
                  onRowClick={(alarm) => open({ kind: 'alarm', id: alarm.id })}
                />
                <Pagination
                  page={alarms.data?.page ?? 1}
                  totalPages={alarms.data?.totalPages ?? 1}
                  total={alarms.data?.total ?? 0}
                  onChange={setPage}
                />
              </>
            )}
          </Card>
        </div>
      )}

      {/* An event is the deeper record, so it wins when both are addressed. */}
      {eventId ? (
        <EventDrawer eventId={eventId} onClose={closeRecord} />
      ) : (
        alarmId && <AlarmDrawer alarmId={alarmId} onClose={closeRecord} />
      )}
    </>
  );
}

/**
 * Says out loud that the list is showing one account's alarms rather than
 * everyone's — a filtered list that does not look filtered is how an operator
 * concludes the population has collapsed overnight.
 */
function ScopeNote({
  deviceId,
  trail,
  onClear,
}: {
  deviceId: string;
  trail: Trail;
  onClear: () => void;
}) {
  const t = useT();

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-brand/25 bg-brand-soft px-3.5 py-2.5">
      <span className="flex flex-wrap items-center gap-2 text-sm text-brand">
        <UserRound size={15} />
        {t('alarms.scopedToUser')}
        <IdChip id={deviceId} />
      </span>
      <div className="flex flex-wrap gap-2">
        <BackButton trail={trail} />
        <Button onClick={onClear}>{t('alarms.showEveryone')}</Button>
      </div>
    </div>
  );
}
